import React, { useState } from 'react'
import { Box, Flex, Loading, Text, useAppTheme } from '@workwhile/ui'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { parseErrorMessage } from 'api'
import { useListingFunnelQuery } from 'queries/listings/useListingFunnelQuery'
import { useShiftDetailContextValue } from '../../../../context/ShiftDetailProvider'
import { ListingEventsFunnelReferencePopover } from './ListingEventsFunnelReferencePopover'
import { ListingFunnelChartTooltip } from './ListingFunnelChartTooltip'
import { ListingFunnelTimeRangeFilter } from './ListingFunnelTimeRangeFilter'
import { ListingFunnelSteps } from './ListingFunnelSteps'
import {
  getDefaultListingFunnelDateRange,
  getListingFunnelMaxEndDate,
} from './utils/getDefaultListingFunnelDateRange'
import { WorkerNotInterestedReasons } from './WorkerNotInterestedReasons'
import { WorkerEventTimeline } from './WorkerEventTimeline'
import { WorkerIneligibilityReasons } from './WorkerIneligibilityReasons'
import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

const panelStyles = {
  border: '1px solid',
  borderColor: 'neutrals.100',
} as const

const sectionStyles = {
  ...panelStyles,
  borderRadius: 'small',
  p: 2,
} as const

const centeredLoadingStyles = {
  justifyContent: 'center',
  alignItems: 'center',
} as const

type Props = {
  listingId: number
}

export const ListingFunnelPanel = ({ listingId }: Props) => {
  const { colors } = useAppTheme()

  const {
    state: {
      createdAt,
      endsAt,
      location: {
        address: { timezone },
      },
    },
  } = useShiftDetailContextValue()

  const defaultDateRange = getDefaultListingFunnelDateRange(createdAt, endsAt)
  const maxEndDate = getListingFunnelMaxEndDate(endsAt)
  const [appliedDateRange, setAppliedDateRange] = useState(defaultDateRange)

  const timeRangeParams = {
    startDate: format(appliedDateRange.from, 'yyyy-MM-dd'),
    endDate: format(appliedDateRange.to, 'yyyy-MM-dd'),
  }

  const handleApplyTimeRange = (dateRange: { from: Date; to: Date }) => {
    setAppliedDateRange({
      from: dateRange.from,
      to:
        dateRange.to.getTime() > maxEndDate.getTime()
          ? maxEndDate
          : dateRange.to,
    })
  }

  const { data, isLoading, isError, error } = useListingFunnelQuery(
    listingId,
    timeRangeParams
  )

  const exportTimeRange = data
    ? { startDate: data.startDate, endDate: data.endDate }
    : timeRangeParams

  const parsedError = parseErrorMessage(error)

  const chartData =
    data?.steps?.map((s) => ({
      name: s.name,
      users: s.users,
      conversion: s.conversionFromPrev,
      eventType: s.eventType,
    })) ?? []

  return (
    <Box
      {...panelStyles}
      borderRadius="standard"
      p={3}
      width="100%"
      height="100%"
    >
      <Flex justifyContent="space-between" alignItems="baseline" mb={2} gap={2}>
        <Text fontSize={2} color="lightText">
          Listing user events funnel <ListingEventsFunnelReferencePopover />
        </Text>
        {data?.dataRefreshedAt ? (
          <Text fontSize={1} color="lightText">
            Last refreshed at{' '}
            {formatInTimeZone(
              new Date(data.dataRefreshedAt),
              timezone,
              'MMM d, h:mm a z'
            )}
          </Text>
        ) : null}
      </Flex>

      <Box mb={3}>
        <ListingFunnelTimeRangeFilter
          defaultDateRange={defaultDateRange}
          appliedDateRange={appliedDateRange}
          maxEndDate={maxEndDate}
          onApply={handleApplyTimeRange}
        />
      </Box>

      {isLoading ? (
        <Flex minHeight={220} {...centeredLoadingStyles}>
          <Loading />
        </Flex>
      ) : isError ? (
        <Text color={colors.errors[200]} fontSize={2}>
          {parsedError ??
            'Failed to load funnel. Please try again or contact eng support.'}
        </Text>
      ) : chartData.length === 0 ? (
        <Text fontSize={2} color="lightText">
          No funnel data available.
        </Text>
      ) : (
        <Box width="100%" height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
              <YAxis allowDecimals={false} />
              <Tooltip content={<ListingFunnelChartTooltip />} />
              <Bar
                dataKey="users"
                fill={colors.primaries[200] || colors.primary200}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {data?.steps?.length ? (
        <ListingFunnelSteps
          listingId={listingId}
          steps={data.steps}
          exportTimeRange={exportTimeRange}
        />
      ) : null}

      <Flex mt={3} gap={3} alignItems="stretch" flexWrap="wrap">
        <Box flex="1 1 280px" minWidth={0} {...sectionStyles}>
          <Text fontSize={2} color="lightText" mb={2}>
            Worker event timeline
          </Text>
          <WorkerEventTimeline
            listingId={listingId}
            timeRange={timeRangeParams}
          />
        </Box>
        <Box flex="1 1 280px" minWidth={0} {...sectionStyles}>
          <Text fontSize={2} color="lightText" mb={2}>
            Worker listing visibility
          </Text>
          <WorkerIneligibilityReasons listingId={listingId} />
        </Box>
        <Box flex="1 1 280px" minWidth={0} {...sectionStyles}>
          <Text fontSize={2} color="lightText" mb={2}>
            Worker not interested reasons
          </Text>
          <WorkerNotInterestedReasons listingId={listingId} />
        </Box>
      </Flex>
    </Box>
  )
}
